import type {InternalAutocompleteSource} from "@algolia/autocomplete-js";
import type {Hit as AlgoliaHit} from "algoliasearch";
import {useAutocomplete} from "../../providers/AutocompleteProvider.tsx";
import {Highlight} from "../Highlight/Highlight";
import {useConfigContext} from "../../../ConfigProvider.tsx";
import type { AlgoliaAttributes } from "../../../types/AlgoliaAttributes";
import {useFormattedCustomerPrice} from "../../../hooks/useFormattedCustomerPrice.tsx";
import {useFormattedCrossPrice} from "../../../hooks/useFormattedCrossPrice.tsx";
import aa from "search-insights";

export function Product({
                            hit,
                            postFixLink,
                            source,
                            position,
                        }: {
    hit: AlgoliaHit<ItemboxAttributes>;
    postFixLink: string;
    source: InternalAutocompleteSource<AlgoliaHit<AlgoliaAttributes>>;
    position: number;
}) {
    const {
        tc
    } = useConfigContext();
    const {autocomplete} = useAutocomplete();
    const itemLink = hit?.itemLink;
    const formattedCustomerPrice = useFormattedCustomerPrice(hit.priceData);
    const formattedCrossPrice = useFormattedCrossPrice(hit.priceData);

    const handleClick = () => {
        // getAlgoliaResults (autocomplete-preset-algolia) enriches every hit with
        // __autocomplete_indexName/__autocomplete_queryID when clickAnalytics is enabled
        // on the query. Without a queryID (e.g. facet-filtered results) we skip the event.
        const insightsHit = hit as AlgoliaHit<ItemboxAttributes> & {
            __autocomplete_queryID?: string;
            __autocomplete_indexName?: string;
        };
        const queryID = insightsHit.__autocomplete_queryID;
        const indexName = insightsHit.__autocomplete_indexName;
        if (!queryID || !indexName) return;

        aa("clickedObjectIDsAfterSearch", {
            eventName: "Autocomplete: Product Clicked",
            index: indexName,
            objectIDs: [hit.objectID],
            positions: [position + 1],
            queryID,
        });
    };

    return (
        <li
            key={`product-${hit.objectID}`}
            className="aa-Item itembox js-updateAlgoliaItemboxElement itemlist"
            data-itemno={hit.itemNo}
            data-item-id={hit.itemId}
            {...autocomplete.getItemProps({item: hit, source})}
            id={`item_${hit.itemId}`}
        >
            <a
                href={`${itemLink}?search_query=${postFixLink}`}
                title={hit.description}
                data-gtm-item={hit.itemId}
                className="itembox__main gtm-product-click"
                onClick={handleClick}
                onAuxClick={handleClick}
            >
                <div className="itemlistContent">
                    <div className="itemlist_description_wrapper">
                        <div className="itemlist_top">
                            <div className="itemlist_name">
                                <Highlight hit={hit} attribute="description"/>
                            </div>
                            <div className="itemlist_description">{hit.summary}</div>
                        </div>
                    </div>

                    <div className="itemlist_price_wrapper">
                        <div className="itemlist_content prices">
                            {formattedCustomerPrice &&
                                (formattedCrossPrice ? (
                                    <>
                      <span className="cross_price">
                        {formattedCustomerPrice}
                      </span>
                                        <span className="base_price">
                        {formattedCrossPrice}
                                            <span>{tc.per_person}</span>
                      </span>
                                    </>
                                ) : (
                                    <span className="base_price">
                      {formattedCustomerPrice}
                                        <span>{tc.per_person}</span>
                    </span>
                                ))}
                        </div>
                    </div>
                </div>

                <div className="itemlist_bottom">
                    <div className="itemlist_info">
                        <div className="itemlist_bottom_details">
                            {hit["attributes.Ort"]?.length && (
                                <div className="itemlist_location">
                                    <i className="iconNew iconNew-map-pin"/>
                                    <span>{hit["attributes.Ort"].join(", ")}</span>
                                </div>
                            )}

                            {hit["attributes.Zeitraum"]?.length > 0 && (
                                <div className="itemlist_date">
                                    <i className="iconNew iconNew-calendar-filter" />
                                    <span>{hit["attributes.Zeitraum"].join(", ")}</span>
                                </div>
                            )}
                        </div>

                        {hit.promotions?.length > 0 && (
                            <div className="itemlist_promotions">
                                {tc.available_as}:{" "}
                                {hit.promotions
                                    .map((p: { description?: string }) =>
                                        p.description ?? String(p)
                                    )
                                    .join(", ")}
                            </div>
                        )}
                    </div>

                    <div className="button button_action">{tc.view_details}</div>
                </div>
            </a>
        </li>
    );
}
